import { useDebouncedValue } from '@mantine/hooks'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '~/components/Button'
import { BaseInputElement } from '~/components/Input'
import { PageContent } from '~/components/PageContent'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { ArchivedContextsTable } from './ArchivedContextsTable'

const SEARCH_DEBOUNCE_TIME = 300

/**
 * The archived contexts, on their own page rather than as a group at the bottom of the list.
 *
 * Archiving is meant to make a context go away, so the list a publisher works in every day shows only the
 * live ones and never carries the archived ones one scroll below them. This page is where they are, and
 * reaching it is owner-only — enforced by the route's `accessRole` and again by the API, which refuses the
 * `enabled` transition to anyone below owner.
 *
 * Restoring is narrower still: only Pubstack may unarchive, the same shape as the archived ad units and
 * medias. Everyone else reads the list.
 */
export const ArchivedContextsPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const breadcrumbs = useBreadcrumbs()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_TIME)

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <Button variant={'tertiary'} iconName={'chevron_left'} onClick={() => navigate('/monetization/one/context/contexts')} trackingLabel={'adm-contexts-archived-back-navigate'}>
          Back to contexts
        </Button>
      }
    >
      <div className={'flex flex-col gap-4'}>
        <div className={'flex items-center gap-4'}>
          <BaseInputElement type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search a context'} value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>

        <ArchivedContextsTable search={debouncedSearch} />
      </div>
    </PageContent>
  )
}
